import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Star,
  Trash2,
  AlertTriangle,
  Mail,
  MailOpen,
  ExternalLink,
  Eye,
  EyeOff,
  ShieldCheck,
  FolderInput,
} from 'lucide-react';
import { emailService } from '../../services';
import { messageHandler } from '../../services/message-handler.service';
import type {
  Email,
  EmailSearchFilters,
  PaginatedResponse,
  EmailCredential,
} from '../../types/email';
import { formatDateTime } from '../../lib/formatters';
import { cn } from '../../lib/utils';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableHeader,
  TableRow,
  Button,
  EmptyState,
  Badge,
  Pagination,
  Modal,
  Form,
  FormField,
  FormLabel,
  FormSelect,
} from '../ui';
import EmailFilters from './EmailFilters';

interface EmailListProps {
  emails: Email[];
  isLoading: boolean;
  filters: EmailSearchFilters;
  onFiltersChange: (filters: EmailSearchFilters) => void;
  pagination?: PaginatedResponse<Email>['paginacao'];
  credentials?: EmailCredential[];
  showCredentialFilter?: boolean;
}

function EmailList({
  emails,
  isLoading,
  filters,
  onFiltersChange,
  pagination,
  credentials = [],
  showCredentialFilter = false,
}: EmailListProps) {
  const [selectedEmail, setSelectedEmail] = useState<Email | null>(null);
  const [showContent, setShowContent] = useState(false);
  const [showMoveModal, setShowMoveModal] = useState(false);
  const [emailToMove, setEmailToMove] = useState<Email | null>(null);
  const queryClient = useQueryClient();

  // Fetch folders for move modal
  const { data: folders } = useQuery({
    queryKey: ['email-folders'],
    queryFn: () => emailService.getFolders(),
    enabled: showMoveModal,
  });

  const { data: emailDetails, isLoading: isLoadingDetails } = useQuery({
    queryKey: ['email-details', selectedEmail?.id],
    queryFn: () => emailService.getReceivedEmailDetails(selectedEmail!.id),
    enabled: !!selectedEmail,
  });

  const markAsReadMutation = useMutation({
    mutationFn: ({ id, isRead }: { id: number; isRead: boolean }) =>
      emailService.markAsRead(id, isRead),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['emails'] });
    },
    onError: error => {
      messageHandler.error(error);
    },
  });

  const toggleStarMutation = useMutation({
    mutationFn: (id: number) => emailService.toggleStar(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['emails'] });
    },
    onError: error => {
      messageHandler.error(error);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => emailService.deleteEmail(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['emails'] });
      messageHandler.success('Email excluído com sucesso');
    },
    onError: error => {
      messageHandler.error(error);
    },
  });

  const spamMutation = useMutation({
    mutationFn: (id: number) => emailService.markAsSpam(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['emails'] });
      messageHandler.success('Email marcado como spam');
    },
    onError: error => {
      messageHandler.error(error);
    },
  });

  const whitelistMutation = useMutation({
    mutationFn: (email: string) => emailService.addToList({ email, action: 'whitelist' }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['emails'] });
      queryClient.invalidateQueries({ queryKey: ['white-black-list'] });
      messageHandler.success('Email adicionado à whitelist');
    },
    onError: error => {
      messageHandler.error(error);
    },
  });

  const moveFolderMutation = useMutation({
    mutationFn: ({ emailId, folderId }: { emailId: number; folderId: number }) =>
      emailService.moveToFolder(emailId, folderId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['emails'] });
      messageHandler.success('Email movido com sucesso');
      setShowMoveModal(false);
      setEmailToMove(null);
    },
    onError: error => {
      messageHandler.error(error);
    },
  });

  const handleDelete = (email: Email) => {
    messageHandler.confirm('Excluir Email', 'Tem certeza que deseja excluir este email?', () =>
      deleteMutation.mutate(email.id)
    );
  };

  const handleViewDetails = (email: Email) => {
    setSelectedEmail(email);
    if (!email.is_read) {
      markAsReadMutation.mutate({ id: email.id, isRead: true });
    }
  };

  const toggleContent = () => {
    setShowContent(prev => !prev);
  };

  const getRowClassName = (email: Email) => {
    const classes = [];

    if (!email.is_read) {
      classes.push('font-semibold');
    }

    if (email.white_black_list === 1) {
      classes.push('bg-green-50 dark:bg-green-900/10');
    } else if (email.white_black_list === 2) {
      classes.push('bg-red-50 dark:bg-red-900/10');
    }

    return cn(classes);
  };

  const canManageList = (email: Email) => {
    return email.white_black_list === 0;
  };

  const handleMoveToFolder = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!emailToMove) return;

    const formData = new FormData(e.currentTarget);
    const folderId = Number(formData.get('folder_id'));

    if (folderId) {
      moveFolderMutation.mutate({
        emailId: emailToMove.id,
        folderId,
      });
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <EmailFilters
        filters={filters}
        onFilterChange={onFiltersChange}
        credentials={credentials}
        showCredentialFilter={showCredentialFilter}
      />

      {emails.length > 0 ? (
        <div className="overflow-x-auto">
          <TableContainer>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="min-w-0">Status</TableHead>
                  <TableHead className="min-w-0">Remetente</TableHead>
                  <TableHead>Assunto</TableHead>
                  <TableHead className="min-w-0">Data</TableHead>
                  <TableHead className="min-w-0">Anexos</TableHead>
                  <TableHead className="min-w-0">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {emails.map(email => (
                  <TableRow key={email.id} className={getRowClassName(email)}>
                    <TableCell>
                      <div className="flex items-center space-x-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => toggleStarMutation.mutate(email.id)}
                          title={email.is_starred ? 'Remover estrela' : 'Marcar com estrela'}
                        >
                          <Star
                            className={cn(
                              'h-4 w-4',
                              email.is_starred ? 'fill-yellow-400 text-yellow-400' : 'text-gray-400'
                            )}
                          />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() =>
                            markAsReadMutation.mutate({ id: email.id, isRead: !email.is_read })
                          }
                          title={email.is_read ? 'Marcar como não lido' : 'Marcar como lido'}
                        >
                          {email.is_read ? (
                            <MailOpen className="h-4 w-4 text-gray-400" />
                          ) : (
                            <Mail className="h-4 w-4 text-primary-600" />
                          )}
                        </Button>
                      </div>
                    </TableCell>
                    <TableCell className="truncate max-w-[200px] overflow-hidden text-ellipsis">
                      {email.sender_email}
                    </TableCell>
                    <TableCell>
                      <div
                        className="truncate max-w-[200px] overflow-hidden text-ellipsis"
                        title={email.subject}
                      >
                        {email.subject}
                      </div>
                    </TableCell>
                    <TableCell className="hidden md:table-cell">
                      {formatDateTime(email.received_at)}
                    </TableCell>
                    <TableCell className="hidden sm:table-cell">
                      {email.attachment_count > 0 && (
                        <Badge variant="secondary">{email.attachment_count}</Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex justify-end space-x-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleViewDetails(email)}
                          title="Ver detalhes"
                        >
                          <ExternalLink className="h-4 w-4 text-primary-600" />
                        </Button>

                        {canManageList(email) && (
                          <>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => whitelistMutation.mutate(email.sender_email)}
                              title="Adicionar à whitelist"
                            >
                              <ShieldCheck className="h-4 w-4 text-green-600" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => spamMutation.mutate(email.id)}
                              title="Marcar como spam"
                            >
                              <AlertTriangle className="h-4 w-4 text-yellow-600" />
                            </Button>
                          </>
                        )}

                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setEmailToMove(email);
                            setShowMoveModal(true);
                          }}
                          title="Mover para pasta"
                        >
                          <FolderInput className="h-4 w-4 text-primary-600" />
                        </Button>

                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDelete(email)}
                          title="Excluir"
                        >
                          <Trash2 className="h-4 w-4 text-red-600" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </div>
      ) : (
        <EmptyState
          icon={<Mail className="h-12 w-12" />}
          title="Nenhum email encontrado"
          description="Não há emails que correspondam aos filtros selecionados."
        />
      )}

      {pagination && pagination.total_paginas > 1 && (
        <Pagination
          currentPage={pagination.pagina_atual}
          totalPages={pagination.total_paginas}
          onPageChange={page => onFiltersChange({ ...filters, page })}
        />
      )}

      {/* Email Details Modal */}
      <Modal
        isOpen={!!selectedEmail}
        onClose={() => setSelectedEmail(null)}
        title="Detalhes do Email"
        size="xl"
      >
        {isLoadingDetails ? (
          <div className="flex justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
          </div>
        ) : emailDetails ? (
          <div className="space-y-6">
            <div className="space-y-2">
              <div className="flex justify-between items-start">
                <div>
                  <p className="text-sm text-gray-500">De:</p>
                  <p className="font-medium">{emailDetails.email.sender_email}</p>
                </div>
                <div className="text-sm text-gray-500">
                  {formatDateTime(emailDetails.email.received_at)}
                </div>
              </div>

              <div>
                <p className="text-sm text-gray-500">Assunto:</p>
                <p className="font-medium">{emailDetails.email.subject}</p>
              </div>

              {emailDetails.email.category && (
                <div>
                  <p className="text-sm text-gray-500">Categoria:</p>
                  <Badge variant="secondary">{emailDetails.email.category}</Badge>
                </div>
              )}

              {emailDetails.email.sentiment && (
                <div>
                  <p className="text-sm text-gray-500">Sentimento:</p>
                  <Badge
                    variant={
                      emailDetails.email.sentiment === 'positive'
                        ? 'success'
                        : emailDetails.email.sentiment === 'negative'
                        ? 'error'
                        : 'warning'
                    }
                  >
                    {emailDetails.email.sentiment === 'positive'
                      ? 'Positivo'
                      : emailDetails.email.sentiment === 'negative'
                      ? 'Negativo'
                      : 'Neutro'}
                  </Badge>
                </div>
              )}

              {emailDetails.email.summary && (
                <div>
                  <p className="text-sm text-gray-500">Resumo:</p>
                  <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-lg mt-2">
                    <p className="whitespace-pre-wrap">{emailDetails.email.summary}</p>
                  </div>
                </div>
              )}

              <div>
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm text-gray-500">Conteúdo:</p>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={toggleContent}
                    className="flex items-center gap-2"
                  >
                    {showContent ? (
                      <>
                        <EyeOff className="h-4 w-4" />
                        Ocultar conteúdo
                      </>
                    ) : (
                      <>
                        <Eye className="h-4 w-4" />
                        Mostrar conteúdo
                      </>
                    )}
                  </Button>
                </div>
                {showContent && (
                  <div
                    className="bg-white dark:bg-gray-800 p-4 rounded-lg overflow-auto max-h-96 prose dark:prose-invert max-w-none"
                    dangerouslySetInnerHTML={{ __html: emailDetails.email.content || '' }}
                  />
                )}
              </div>

              {emailDetails.email.attachments?.length > 0 && (
                <div>
                  <h3 className="font-medium mb-2">Anexos:</h3>
                  <div className="space-y-2">
                    {emailDetails.email.attachments.map((attachment, index) => (
                      <div
                        key={index}
                        className="flex items-center justify-between p-2 bg-gray-50 dark:bg-gray-800 rounded"
                      >
                        <span className="text-sm">{attachment.filename}</span>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => window.open(attachment.file_path, '_blank')}
                        >
                          Download
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {emailDetails.replies?.length > 0 && (
                <div>
                  <h3 className="font-medium mb-2">Respostas:</h3>
                  <div className="space-y-4">
                    {emailDetails.replies.map(reply => (
                      <div key={reply.id} className="bg-gray-50 dark:bg-gray-800 p-4 rounded-lg">
                        <div className="flex justify-between items-start mb-2">
                          <div>
                            <p className="text-sm text-gray-500">Para:</p>
                            <p>{reply.to_email}</p>
                          </div>
                          <div className="text-sm text-gray-500">
                            {formatDateTime(reply.sent_at)}
                          </div>
                        </div>
                        <div className="mb-2">
                          <p className="text-sm text-gray-500">Assunto:</p>
                          <p>{reply.subject}</p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-500">Conteúdo:</p>
                          <p className="whitespace-pre-wrap">{reply.body}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="mt-6 flex justify-end">
              <Button variant="secondary" onClick={() => setSelectedEmail(null)}>
                Fechar
              </Button>
            </div>
          </div>
        ) : null}
      </Modal>

      {/* Move to Folder Modal */}
      <Modal
        isOpen={showMoveModal}
        onClose={() => {
          setShowMoveModal(false);
          setEmailToMove(null);
        }}
        title="Mover para Pasta"
        size="sm"
      >
        <Form onSubmit={handleMoveToFolder}>
          <FormField>
            <FormLabel htmlFor="folder_id" required>
              Pasta
            </FormLabel>
            <FormSelect id="folder_id" name="folder_id" required>
              <option value="">Selecione uma pasta</option>
              {folders?.map(folder => (
                <option
                  key={folder.id}
                  value={folder.id}
                  disabled={folder.id === emailToMove?.folder_id}
                >
                  {folder.name}
                </option>
              ))}
            </FormSelect>
          </FormField>

          <div className="mt-6 flex justify-end space-x-2">
            <Button type="submit" isLoading={moveFolderMutation.isPending}>
              Mover
            </Button>
            <Button
              type="button"
              variant="secondary"
              onClick={() => {
                setShowMoveModal(false);
                setEmailToMove(null);
              }}
            >
              Cancelar
            </Button>
          </div>
        </Form>
      </Modal>
    </div>
  );
}

export default EmailList;
